
// @ts-ignore
    import __i18nConfig from '@next-translate-root/i18n'
// @ts-ignore
    import __loadNamespaces from 'next-translate/loadNamespaces'
// @ts-ignore
    
import React from "react";
import useTranslation from "next-translate/useTranslation";
import Trans from "next-translate/Trans";

import { Head } from "@/components/Head";
import { useTheme } from "@/hooks/useTheme";
import { Converter } from "@/containers/Converter";

import { MagicianSection } from "@/sections/MagicianSection";
import { TrustedBySection } from "@/sections/TrustedBySection";
import { SelectFormatSection } from "@/sections/SelectFormatSection";
import { CTASection } from "@/sections/CTASection";
import { ApiTeaserSection } from "@/sections/ApiTeaserSection";
import { ConversionSection } from "@/sections/ConversionSection";
import { ShareDesignSection } from "@/sections/ShareDesignSection";
import { ConverterFaqSection } from "@/sections/ConverterFaqSection";

const CanvaToPowerpoint = () => {
  const { t } = useTranslation();
  const { colors } = useTheme();

  return (
    <>
      <Head
        pageNamespace="page/CanvaToPowerpoint"
        ogImage="/images/canva2powerpoint-og-image.jpg"
      />

      <MagicianSection
        title={t("page/CanvaToPowerpoint:title")}
        subtitle={t("page/CanvaToPowerpoint:subtitle")}
        // TODO: the glow is not working in Chrome
        magicianLogoFile="powerpoint"
        cta={{
          title: t("section/magicians:seeHowItWorks"),
          scrollToId: "ApiTeaserSection",
        }}
      >
        <Converter />
      </MagicianSection>

      <TrustedBySection />

      <ApiTeaserSection
        title={
          ((
            <Trans
              i18nKey="page/CanvaToPowerpoint:section/apiTeaser.title"
              components={[<span style={{ color: colors.ORANGE }} key={0} />]}
            />
          ) as unknown) as string
        }
        image={{
          src: "/images/canva-to-powerpoint-example.png",
          alt: "Canva to Powerpoint converter example",
        }}
        backgroundColor={colors.LIGHT_GRAY1}
        cta={{
          title: t("common:actions.signUp"),
        }}
        elements={t(
          "page/CanvaToPowerpoint:section/apiTeaser.apiTeaserElements",
          null,
          {
            returnObjects: true,
          },
        )}
      />
      <ShareDesignSection
        id="ShareDesignSection"
        title={
          ((
            <Trans
              i18nKey="page/CanvaToPowerpoint:section/shareDesign.title"
              components={[<span key="0" style={{ color: colors.ORANGE }} />]}
            />
          ) as unknown) as string
        }
        image={{
          src: "/images/canva-to-powerpoint-converted-file-elements.jpg",
          alt: t("page/CanvaToPowerpoint:section/shareDesign.imageAlt"),
        }}
        backgroundColor={colors.LIGHT_GRAY1}
        cta={{
          title: t("page/CanvaToPowerpoint:section/shareDesign.ctaButton"),
        }}
        elements={[
          {
            id: "ELEMENT#1",
            iconType: "SPREAD-ICON",
            title: t(
              "page/CanvaToPowerpoint:section/shareDesign.elements.pathElementsTitle",
            ),
            text: t(
              "page/CanvaToPowerpoint:section/shareDesign.elements.pathElementsText",
            ),
          },
          {
            id: "ELEMENT#2",
            iconType: "FOLDER-SIMPLE-ICON",
            title: t(
              "page/CanvaToPowerpoint:section/shareDesign.elements.folderStructureTitle",
            ),
            text: t(
              "page/CanvaToPowerpoint:section/shareDesign.elements.folderStructureText",
            ),
          },
          {
            id: "ELEMENT#3",
            iconType: "CHECK-GREEN-ICON",
            title: t(
              "page/CanvaToPowerpoint:section/shareDesign.elements.imageSupportTitle",
            ),
            text: t(
              "page/CanvaToPowerpoint:section/shareDesign.elements.imageSupportText",
            ),
          },
        ]}
      />

      <ConversionSection
        title={t("section/conversion:title")}
        iconBadges={[
          {
            iconType: "SHEETS",
            text: t("section/conversion:badges.supportsAll"),
          },
          {
            iconType: "CHECK-GREEN-ICON",
            text: t("section/conversion:badges.accuracy"),
          },
        ]}
        elements={{
          from: {
            iconType: "CANVA",
            image: "/images/canva2ppt-canva-preview.jpg",
            title: "Canva Demo Preview",
          },
          to: {
            iconType: "POWERPOINT",
            image: "/images/canva2ppt-ppt-preview.jpg",
            title: "PowerPoint Demo Preview",
          },
        }}
      />

      <ConverterFaqSection
        title={t("page/CanvaToPowerpoint:section/faq.title")}
        items={[
          {
            question: t(
              "page/CanvaToPowerpoint:section/faq.howToConvert.question",
            ),
            answer: (
              <Trans
                i18nKey="page/CanvaToPowerpoint:section/faq.howToConvert.answer"
                components={[
                  // eslint-disable-next-line jsx-a11y/anchor-has-content
                  <a
                    href="/blog/how-to-convert-canva-to-ppt-a-step-by-step-guide"
                    key="0"
                  />,
                ]}
              />
            ),
            answerText: t(
              "page/CanvaToPowerpoint:section/faq.howToConvert.answer",
            ),
            icon: "SWITCH-ICON",
          },
          {
            question: t("page/CanvaToPowerpoint:section/faq.free.question"),
            answer: t("page/CanvaToPowerpoint:section/faq.free.answer"),
            answerText: t("page/CanvaToPowerpoint:section/faq.free.answer"),
            icon: "FREE-ICON",
          },
          {
            question: t("page/CanvaToPowerpoint:section/faq.editable.question"),
            answer: t("page/CanvaToPowerpoint:section/faq.editable.answer"),
            answerText: t("page/CanvaToPowerpoint:section/faq.editable.answer"),
            icon: "PEN-ICON",
          },
          {
            question: t("page/CanvaToPowerpoint:section/faq.fromLink.question"),
            answer: (
              <Trans
                i18nKey="page/CanvaToPowerpoint:section/faq.fromLink.answer"
                components={[
                  // eslint-disable-next-line jsx-a11y/anchor-has-content
                  <a
                    href="/blog/how-to-temporarily-make-a-canva-design-public"
                    key="0"
                  />,
                ]}
              />
            ),
            answerText: t("page/CanvaToPowerpoint:section/faq.fromLink.answer"),
            icon: "FILE-ICON",
          },
          {
            question: t(
              "page/CanvaToPowerpoint:section/faq.animations.question",
            ),
            answer: t("page/CanvaToPowerpoint:section/faq.animations.answer"),
            answerText: t(
              "page/CanvaToPowerpoint:section/faq.animations.answer",
            ),
            icon: "TIME-BLUE-ICON",
          },
        ]}
      />

      <SelectFormatSection title={t("section/selectFormat:title")} />

      <CTASection
        title={t("section/cta:titles.canvaToPowerpointPage")}
        href="#upload"
      />
    </>
  );
};

// Temporal workaround for https://github.com/vercel/next.js/discussions/18396
 async function _getStaticProps() {
  return {
    props: {},
  };
}

export default CanvaToPowerpoint;


// @ts-ignore
    export async function getStaticProps(ctx) {
// @ts-ignore
        let res = _getStaticProps(ctx)
// @ts-ignore
        if(typeof res.then === 'function') res = await res
// @ts-ignore
        return {
// @ts-ignore
          
// @ts-ignore
          ...res,
// @ts-ignore
          props: {
// @ts-ignore
            ...(res.props || {}),
// @ts-ignore
            ...(await __loadNamespaces({
// @ts-ignore
              ...ctx,
// @ts-ignore
              pathname: '/converter/canva-to-powerpoint',
// @ts-ignore
              loaderName: 'getStaticProps',
// @ts-ignore
              ...__i18nConfig,
// @ts-ignore
              
// @ts-ignore
            }))
// @ts-ignore
          }
// @ts-ignore
        }
// @ts-ignore
    }
// @ts-ignore
  